import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, CheckCircle, Eye, EyeOff, FileText, Loader2, ShieldCheck } from 'lucide-react'
import apiService from '../services/api'
import toast from 'react-hot-toast'
import { validatePassword } from '../validation'
import SchoolTermsAcceptance from '../components/SchoolTermsAcceptance'

type ClaimProfileLocationState = {
  email?: string
}

const ClaimProfile: React.FC = () => {
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const emailFromState = (location.state as ClaimProfileLocationState | null)?.email || ''
  const emailFromQuery = (searchParams.get('email') || '').trim().toLowerCase()
  const normalizedEmail = useMemo(
    () => emailFromState.trim().toLowerCase() || emailFromQuery,
    [emailFromQuery, emailFromState],
  )

  const [email] = useState(normalizedEmail)
  const [isLoading, setIsLoading] = useState(false)

  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const otpRefs = useRef<(HTMLInputElement | null)[]>([])

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [termsError, setTermsError] = useState<string | null>(null)

  useEffect(() => {
    if (!email) {
      toast.error('Please start from Claim profile and enter your email.')
      navigate('/claim-profile', { replace: true })
      return
    }
    otpRefs.current[0]?.focus()
  }, [email, navigate])

  const extractMessage = useMemo(
    () => (error: any, fallback: string): string => {
    const errData = error?.response?.data
    const errObj = errData?.error
    const msg =
      (typeof errObj === 'object' && errObj?.message) ||
      (typeof errObj === 'string' && errObj) ||
      errData?.message ||
      error?.message ||
      fallback
    return String(msg)
    },
    [],
  )

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return
    const next = [...otp]
    next[index] = value.replace(/\D/g, '')
    setOtp(next)
    if (value && index < 5) otpRefs.current[index + 1]?.focus()
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus()
    }
  }

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    const next = [...otp]
    for (let i = 0; i < 6; i++) next[i] = digits[i] || ''
    setOtp(next)
    otpRefs.current[Math.min(digits.length - 1, 5)]?.focus()
  }

  const handleComplete = async (e: React.FormEvent) => {
    e.preventDefault()
    const code = otp.join('')
    if (code.length !== 6) {
      toast.error('Please enter the full 6-digit code')
      return
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }
    const pwdErr = validatePassword(password)
    if (pwdErr) {
      toast.error(pwdErr)
      return
    }
    if (!termsAccepted) {
      setTermsError('You must accept the Terms of Service and Privacy Policy to claim your profile.')
      return
    }
    setTermsError(null)

    setIsLoading(true)
    try {
      const response = await apiService.claimProfileComplete(email, code, password, true)
      if (response?.success) {
        toast.success(
          'Account ready! After you sign in, upload your school verification documents (business registration, address proof) so we can complete your listing.',
          { duration: 7000 }
        )
        navigate('/login', { replace: true, state: { email, afterClaimProfile: true } })
      } else {
        throw new Error(response?.message || 'Failed to claim profile')
      }
    } catch (error: any) {
      toast.error(extractMessage(error, 'Failed to claim profile'))
      // On an invalid/expired OTP, clear the boxes to invite a retry.
      const status = error?.response?.status
      if (status === 400 || status === 422) {
        setOtp(['', '', '', '', '', ''])
        otpRefs.current[0]?.focus()
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async () => {
    if (!email) return
    setIsLoading(true)
    try {
      const response = await apiService.claimProfileSendOTP(email)
      if (response?.success) {
        toast.success('Verification code resent')
        setOtp(['', '', '', '', '', ''])
        otpRefs.current[0]?.focus()
      }
    } catch (error: any) {
      toast.error(extractMessage(error, 'Failed to resend verification code'))
    } finally {
      setIsLoading(false)
    }
  }

  const inputClass =
    'w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-900 placeholder-gray-400 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all'

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="mx-auto flex items-center justify-center mb-4">
            <img src="/logo.png" alt="BookMyYogaTTC Logo" className="h-20 w-auto object-contain" />
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-100 border border-primary-200 text-primary-800 text-xs font-medium">
            <ShieldCheck className="h-3.5 w-3.5" />
            Claim profile
          </div>
          <h2 className="mt-4 text-3xl font-bold text-gray-900">
            Verify your email
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            We&apos;ve sent a 6-digit code to <strong>{email}</strong>. Enter it below and set your password.
          </p>
          <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-left text-sm text-amber-950">
            <div className="flex gap-2">
              <FileText className="h-5 w-5 shrink-0 text-amber-700 mt-0.5" aria-hidden />
              <div>
                <p className="font-semibold text-amber-900">After you claim this account</p>
                <p className="mt-1 text-amber-900/90 leading-relaxed">
                  Claiming only unlocks your login. You still need to{' '}
                  <strong>upload verification documents</strong> (e.g. business registration and address proof) under{' '}
                  <strong>Profile → School verification documents</strong> so our team can review and activate your listing.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-6 space-y-6"
            onSubmit={handleComplete}
          >
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2 text-center">
                Verification code
              </label>
              <div className="flex justify-center gap-2">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      otpRefs.current[index] = el
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    onPaste={handleOtpPaste}
                    className="w-11 h-13 text-center text-xl font-bold border-2 border-gray-200 rounded-lg py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                    autoFocus={index === 0}
                  />
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-xs font-medium text-gray-600 mb-1.5">
                  New password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={inputClass + ' pr-10'}
                    placeholder="Min. 8 characters"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="mt-1 text-[11px] text-gray-500">
                  At least 8 characters with uppercase, lowercase, a number and a special character.
                </p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-xs font-medium text-gray-600 mb-1.5">
                  Confirm password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={inputClass + ' pr-10'}
                    placeholder="Re-enter your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-gray-400 hover:text-gray-600"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirmPassword && password === confirmPassword && (
                  <p className="mt-1 text-xs text-primary-600 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-primary-600" />
                    Passwords match
                  </p>
                )}
              </div>
            </div>

            <SchoolTermsAcceptance
              idPrefix="claim-verify"
              accepted={termsAccepted}
              onAcceptedChange={(v) => {
                setTermsAccepted(v)
                if (v) setTermsError(null)
              }}
              error={termsError || undefined}
            />

            <button
              type="submit"
              disabled={isLoading || otp.join('').length !== 6 || !termsAccepted}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-semibold text-white bg-primary-600 rounded-xl hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Claim profile & set password'}
            </button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={() => navigate('/claim-profile', { replace: true })}
                className="inline-flex items-center text-primary-600 hover:text-primary-500 font-medium"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Use a different email
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={isLoading}
                className="font-medium text-primary-600 hover:text-primary-500 disabled:opacity-50"
              >
                Resend code
              </button>
            </div>
          </motion.form>
      </div>
    </div>
  )
}

export default ClaimProfile
